import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { MATCAP_GAIN } from './lib/matcap.js';
import { genererMatcap } from './totem.js';
import { SceneClock } from './lib/scene-clock.js';

// Contrat du rig partagé : interdit. La lumière est dans la matcap et les arêtes.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'formation',
  family: 'geometric',
  danceability: 0.84,
  requiresTier: 0,
  energyLevel: 0.82,
  bpmMin: 115,
  bpmMax: 175,
  movement: 0.90,
  tonality: 0.40,
  enabled: true
};

// Formation (le brief disait « Swarm » ; cet id est pris par l'essaim de
// points de visualizers/swarm.js) : des milliers d'objets 3D - cubes,
// pyramides, anneaux, sphères, chromés ou néon - qui volent en FORMATION
// autour de la caméra : sphère, hélice, tunnel, mur, vortex, et le TITRE une
// fois par scène. Sur chaque mesure ils quittent leur place et filent vers la
// formation suivante, avec des traînées. Preset party de vdjLook. Techno,
// trance, EDM, drum'n'bass.
//
// TOUT DANS LE VERTEX SHADER : une formation est une fonction (indice ->
// position) ; chaque objet connaît son indice (attribut statique aIdx) et
// trois hasards (aRand) ; sa position = Bézier quadratique de la formation
// source à la cible, point de contrôle décalé de côté (le hasard), avec un
// RETARD proportionnel à sa distance au centre dans la source : la nuée se
// réorganise en VAGUE, jamais en saut. Aucun upload par objet, jamais.
// Rotation propre (axe et vitesse par aRand), taille qui pulse avec la bande
// du groupe (indice mod 3 : graves, médiums, aigus).
//
// FORMATIONS : sphère de Fibonacci qui tourne ; double hélice le long de
// l'axe de la caméra qui défile ; tunnel d'anneaux (la caméra vole dedans) ;
// mur-grille face à la caméra ondulé par le spectre ; vortex qui aspire vers
// le centre ; TITRE : points échantillonnés sur les glyphes (canvas, police
// Inter 800) livrés en texture de données au changement de morceau (R17),
// les objets en trop filent loin derrière.
//
// MATÉRIAUX : CHROME = la matcap de Totem (lib/matcap.js : 40 % d'ombre,
// deux boîtes à lumière larges, liseré de Fresnel plafonné, aucun pastel),
// teintée par la palette ; NÉON = faces sombres, arêtes dessinées EN
// DISTANCE EN PIXELS (R1 : cœur 1,5 px, gaine 2-5 px, halo exp(-d/8)) - le
// cube par la distance analytique à ses 12 arêtes (pas de diagonales), les
// autres par les barycentriques de leurs triangles (fil de fer). Contours
// nets : cible MSAA privée (R5), sans AA au tier 0.
//
// TRAÎNÉES : pendant les transitions seulement, 4 segments par objet, la
// position évaluée aux 4 progressions précédentes (forme close : rien à
// mémoriser), lignes additives fines. Pas de traînées au tier 0.
//
// CAMÉRA : la caméra de la scène, en orbite lente autour de la nuée (sphère,
// mur, vortex, titre) ou DANS la nuée près de l'axe (hélice, tunnel), le plan
// changé sur les phrases, recul de 3 % sur chaque kick, glissé d'un mode à
// l'autre pendant la transition. Le centre de la nuée est le centre de la
// scène (R19) ; le masque safeZone suit la pochette.
//
// RYTHME : temps = pulsation de taille par groupe, flash des arêtes néon,
// séparation RGB vdj ; mesure = formation suivante ; phrase = type d'objet
// (un, ou un mélange de deux), matériau, palette ; montée = tout en sphère
// de plus en plus petite qui tourne de plus en plus vite ; drop = explosion
// radiale à travers la caméra, flash, puis le tunnel à pleine vitesse.
// SCÈNE (R20) : formée dès la première image ; une mesure = une formation,
// donc 3 au moins par scène de 16 s ; le titre une fois par scène.
//
// Debug : ?swarmform=sphere|helix|tunnel|wall|vortex|text,
// ?swarmcount=<n>, ?swarmmat=chrome|neon.

const FORMS = ['sphere', 'helix', 'tunnel', 'wall', 'vortex', 'text'];
const INSIDE = { tunnel: true };
// the camera's azimuth per formation: free orbit (null), or in front (the wall, the vortex, the title)
const FRONT_AZ = { wall: Math.PI / 2, vortex: Math.PI / 2 + 0.25, text: Math.PI / 2 };
const TIER = { 0: { count: 800, trails: false, msaa: 0 }, 1: { count: 1500, trails: true, msaa: 4 }, 2: { count: 3000, trails: true, msaa: 4 } };
const MAX_COUNT = 3000;
const TITLE_TEX_W = 64, TITLE_TEX_H = 48;
const SCENE_S = 16;
const SAFE_FLOOR = 0.4;
const FALLBACK = [0x00e5ff, 0xff2bd6, 0xffe14d];
const KICK_ATTACK = 0.05, KICK_TAU = 0.12;

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
const clamp01 = (x) => Math.max(0, Math.min(1, x));
const lerp = (a, b, t) => a + (b - a) * t;

// ------------------------------------------------------------------ the formations (GLSL)
const FORMATION_GLSL = `
  uniform float uN, uTitleN;
  uniform sampler2D uTitle;
  uniform float uBuild, uSpeed;
  uniform vec3 uBands;
  const float GOLD = 2.39996323;
  vec3 formation(float f, float i, float ph, vec3 rnd) {
    float t = (i + 0.5) / uN;
    if (f < 0.5) {                                   // SPHERE: Fibonacci, turning; the build shrinks it
      float y = 1.0 - 2.0 * t;
      float r = sqrt(max(0.0, 1.0 - y * y));
      float a = i * GOLD + ph;
      float R = 3.2 * (1.0 - 0.62 * uBuild);
      return R * vec3(r * cos(a), y, r * sin(a));
    }
    if (f < 1.5) {                                   // HELIX: two strands along the camera axis, scrolling
      float s = mod(t + ph * 0.05, 1.0);
      float strand = mod(i, 2.0);
      float a = s * 6.0 * 6.2831853 + strand * 3.14159265 + ph * 0.3;
      float z = mix(-7.0, 7.0, s);
      return vec3(cos(a) * 2.2, sin(a) * 2.2, z);
    }
    if (f < 2.5) {                                   // TUNNEL: rings, the camera flies through
      float ring = floor(i / 40.0), m = mod(i, 40.0);
      float a = m / 40.0 * 6.2831853 + ring * 0.12 + ph * 0.1;
      float R = 3.0 + 0.25 * sin(ring * 0.7 + ph);
      float L = (uN / 40.0) * 0.7;
      float z = mod(ring * 0.7 + ph * 1.2, L);
      return vec3(cos(a) * R, sin(a) * R, 3.0 - z);
    }
    if (f < 3.5) {                                   // WALL: a grid facing the camera, a spectrum ripple
      float cols = 60.0;
      float c = mod(i, cols), r = floor(i / cols);
      float rows = ceil(uN / cols);
      float x = (c - cols * 0.5 + 0.5) / cols * 13.0, y = (r - rows * 0.5 + 0.5) / max(rows, 1.0) * 7.4;
      float band = abs(x) < 4.3 ? uBands.x : (abs(x) < 8.7 ? uBands.y : uBands.z);
      float z = -3.0 + 1.1 * sin(x * 1.0 + ph * 2.0) * (0.4 + band) + 0.4 * sin(y * 1.4 - ph * 1.3);
      return vec3(x, y, z);
    }
    if (f < 4.5) {                                   // VORTEX: a spiral sucked toward the centre
      float s = mod(t - ph * 0.03, 1.0);
      float a = s * 28.0 + ph * 1.5 + rnd.x * 0.3;
      float R = 0.3 + 5.4 * pow(s, 0.8);
      return vec3(cos(a) * R, sin(a) * R * 0.75, -3.5 + (1.0 - s) * 2.5);
    }
    // TEXT: the title's points (16-bit xy in a data texture); the rest far behind
    if (i < uTitleN) {
      vec2 uv = (vec2(mod(i, ${TITLE_TEX_W}.0), floor(i / ${TITLE_TEX_W}.0)) + 0.5) / vec2(${TITLE_TEX_W}.0, ${TITLE_TEX_H}.0);
      vec4 q = texture2D(uTitle, uv);
      float x = (q.r * 255.0 * 256.0 + q.g * 255.0) / 65535.0, y = (q.b * 255.0 * 256.0 + q.a * 255.0) / 65535.0;
      return vec3((x - 0.5) * 8.6, (y - 0.5) * 2.15, -0.5 + 0.12 * sin(i * 0.37 + ph));
    }
    float y = 1.0 - 2.0 * t, r = sqrt(max(0.0, 1.0 - y * y)), a = i * GOLD + ph;
    return 28.0 * vec3(r * cos(a), y, r * sin(a)) + vec3(0.0, 0.0, -30.0);
  }
  uniform float uFormA, uFormB, uProg, uPhA, uPhB, uExplode;
  // the object's place at progress p: a quadratic Bezier from A to B, delayed by its distance to the centre
  vec3 placeAt(float i, vec3 rnd, float p) {
    vec3 A = formation(uFormA, i, uPhA, rnd);
    vec3 B = formation(uFormB, i, uPhB, rnd);
    float delay = 0.4 * clamp(length(A) / 6.0, 0.0, 1.0);
    float s = clamp((p - delay) / 0.6, 0.0, 1.0);
    s = s * s * (3.0 - 2.0 * s);
    vec3 d = B - A;
    vec3 side = normalize(cross(d + vec3(0.001, 0.0, 0.0), rnd * 2.0 - 1.0) + vec3(0.0, 0.001, 0.0));
    vec3 C = (A + B) * 0.5 + side * length(d) * (0.25 + 0.3 * rnd.z);
    vec3 P = (1.0 - s) * (1.0 - s) * A + 2.0 * (1.0 - s) * s * C + s * s * B;
    // the drop: thrown radially through the camera
    P += normalize(P + vec3(0.0, 0.0, 0.01)) * uExplode * 9.0;
    return P;
  }
`;

const objectVertex = `
  precision highp float;
  attribute float aIdx;
  attribute vec3 aRand;
  attribute vec3 aBary;
  ${FORMATION_GLSL}
  uniform float uTime, uSize;
  uniform vec3 uPulse;
  varying vec3 vNormalV, vLocal, vBary;
  varying float vGroup;
  mat3 rotAxis(vec3 ax, float an) {
    float c = cos(an), s = sin(an), k = 1.0 - c;
    return mat3(c + ax.x * ax.x * k, ax.y * ax.x * k + ax.z * s, ax.z * ax.x * k - ax.y * s,
                ax.x * ax.y * k - ax.z * s, c + ax.y * ax.y * k, ax.z * ax.y * k + ax.x * s,
                ax.x * ax.z * k + ax.y * s, ax.y * ax.z * k - ax.x * s, c + ax.z * ax.z * k);
  }
  void main() {
    vec3 P = placeAt(aIdx, aRand, uProg);
    float g = mod(aIdx, 3.0);
    float pulse = g < 0.5 ? uPulse.x : (g < 1.5 ? uPulse.y : uPulse.z);
    float size = uSize * (0.8 + 0.4 * aRand.x) * (1.0 + 0.35 * pulse);
    mat3 R = rotAxis(normalize(aRand - 0.5 + vec3(0.0, 0.01, 0.0)), uTime * (0.6 + 1.4 * aRand.y) * uSpeed + aRand.z * 6.28);
    vec3 local = R * (position * size);
    vec4 mv = modelViewMatrix * vec4(P + local, 1.0);
    vNormalV = normalize(normalMatrix * (R * normal));
    vLocal = position;
    vBary = aBary;
    vGroup = g;
    gl_Position = projectionMatrix * mv;
  }
`;

const chromeFragment = `
  precision highp float;
  uniform sampler2D uMatcap;
  uniform float uExposure;
  varying vec3 vNormalV, vLocal, vBary;
  varying float vGroup;
  void main() {
    vec3 n = normalize(vNormalV);
    if (n.z < 0.0) n = normalize(vec3(n.xy, 0.0) + vec3(0.0, 0.0, 0.02));
    vec3 c = texture2D(uMatcap, n.xy * 0.49 + 0.5).rgb;
    c = c * c * ${MATCAP_GAIN.toFixed(2)};
    gl_FragColor = vec4(c * uExposure, 1.0);
  }
`;

const neonFragment = `
  precision highp float;
  uniform vec3 uCol0, uCol1, uCol2;
  uniform float uFlash, uType, uExposure;
  varying vec3 vNormalV, vLocal, vBary;
  varying float vGroup;
  void main() {
    vec3 col = vGroup < 0.5 ? uCol0 : (vGroup < 1.5 ? uCol1 : uCol2);
    float d;
    if (uType < 0.5) {
      // the cube: the distance to its 12 edges (the two largest |p| toward 0.5)
      vec3 a = abs(vLocal);
      float m1 = max(a.x, max(a.y, a.z));
      float m3 = min(a.x, min(a.y, a.z));
      float m2 = a.x + a.y + a.z - m1 - m3;
      d = length(vec2(0.5 - m1, 0.5 - m2));
    } else {
      d = min(vBary.x, min(vBary.y, vBary.z));
    }
    float dpx = d / max(fwidth(d), 1e-5);
    float core = 1.0 - smoothstep(0.75, 1.75, dpx);
    float sheath = 1.0 - smoothstep(1.5, 5.0, dpx);
    float halo = exp(-dpx / 8.0) * 0.25;
    // faces dark, tinted; the Fresnel of the face a little lighter
    vec3 face = col * 0.035 * (1.0 + 2.0 * pow(1.0 - abs(normalize(vNormalV).z), 2.0));
    vec3 c = face + col * (0.7 * sheath * (1.0 + 0.5 * uFlash) + halo) + mix(col, vec3(1.0), 0.55) * core * (0.72 + 0.2 * uFlash);
    gl_FragColor = vec4(c * uExposure, 1.0);
  }
`;

const trailVertex = `
  precision highp float;
  attribute float aIdx;
  attribute vec3 aRand;
  attribute vec3 aK;                    // x: the segment (0..3), y: its end (0/1)
  ${FORMATION_GLSL}
  uniform float uDProg;
  varying float vFade;
  void main() {
    float back = (aK.x + aK.y) * uDProg;
    vec3 P = placeAt(aIdx, aRand, max(uProg - back, 0.0));
    vFade = 1.0 - (aK.x + aK.y) / 5.0;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(P, 1.0);
  }
`;
const trailFragment = `
  precision mediump float;
  uniform vec3 uTrailCol;
  uniform float uTrailK;
  varying float vFade;
  void main() { gl_FragColor = vec4(uTrailCol * vFade * uTrailK, 1.0); }
`;

const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const displayFragment = `
  precision highp float;
  uniform sampler2D uTex;
  uniform float uAspect, uWhite;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  void main() {
    vec3 col = texture2D(uTex, vUv).rgb;
    col = mix(col, vec3(1.0), uWhite);
    vec2 s2 = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s2 - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
    gl_FragColor = vec4(col, 1.0);
  }
`;

// ------------------------------------------------------------------ geometry
function baryGeometry(base) {
  const g = base.toNonIndexed();
  const n = g.attributes.position.count;
  const b = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) b[i * 3 + (i % 3)] = 1;
  g.setAttribute('aBary', new THREE.BufferAttribute(b, 3));
  return g;
}
function instanced(base, idx, rnd) {
  const g = new THREE.InstancedBufferGeometry().copy(baryGeometry(base));
  g.setAttribute('aIdx', new THREE.InstancedBufferAttribute(idx, 1));
  g.setAttribute('aRand', new THREE.InstancedBufferAttribute(rnd, 3));
  g.instanceCount = MAX_COUNT;
  return g;
}
// the title's points: a canvas, Inter 800, sampled where the ink is, 16-bit xy
function titlePoints(text, count) {
  const cv = document.createElement('canvas'); cv.width = 1024; cv.height = 256;
  const x = cv.getContext('2d');
  x.fillStyle = '#000'; x.fillRect(0, 0, cv.width, cv.height);
  let size = 170;
  x.font = `800 ${size}px Inter, Helvetica, Arial, sans-serif`;
  while (x.measureText(text).width > cv.width * 0.92 && size > 40) { size -= 6; x.font = `800 ${size}px Inter, Helvetica, Arial, sans-serif`; }
  x.fillStyle = '#fff'; x.textAlign = 'center'; x.textBaseline = 'middle';
  x.fillText(text, cv.width / 2, cv.height / 2);
  const img = x.getImageData(0, 0, cv.width, cv.height).data;
  const pts = [];
  for (let j = 0; j < cv.height; j += 2) for (let i = 0; i < cv.width; i += 2) if (img[(j * cv.width + i) * 4] > 128) pts.push([(i + 0.5) / cv.width, 1 - (j + 0.5) / cv.height]);
  if (!pts.length) return null;
  // a fixed-seed subset of `count` points, spread (stride), not a random clump
  const out = new Uint8Array(TITLE_TEX_W * TITLE_TEX_H * 4);
  const n = Math.min(count, pts.length, TITLE_TEX_W * TITLE_TEX_H);
  let seed = 17;
  const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  for (let k = 0; k < n; k++) {
    const p = pts[Math.min(pts.length - 1, Math.floor((k + rnd()) / n * pts.length))];
    const X = Math.round(p[0] * 65535), Y = Math.round(p[1] * 65535);
    out[k * 4] = X >> 8; out[k * 4 + 1] = X & 255; out[k * 4 + 2] = Y >> 8; out[k * 4 + 3] = Y & 255;
  }
  const t = new THREE.DataTexture(out, TITLE_TEX_W, TITLE_TEX_H, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace; t.minFilter = t.magFilter = THREE.NearestFilter; t.generateMipmaps = false; t.needsUpdate = true;
  return { texture: t, n };
}

export class FormationVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const fc = parseInt(urlParam('swarmcount'), 10);
    this._count = Number.isFinite(fc) && fc > 0 ? Math.min(MAX_COUNT, fc) : this._layout.count;
    const ff = urlParam('swarmform'); this._forceForm = FORMS.indexOf(ff) >= 0 ? FORMS.indexOf(ff) : null;
    const fm = urlParam('swarmmat'); this._forceMat = fm === 'chrome' || fm === 'neon' ? fm : null;
    this._post = new VdjPost({ bloomStrength: 0.5, bloomThreshold: 0.78, grain: 0.0, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
    this._clock = new SceneClock({ period: SCENE_S, start: 'random' });
  }

  get postProcessing() { return this._post.value; }
  _rnd() { this._seed = (this._seed * 16807) % 2147483647; return this._seed / 2147483647; }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    this._seed = 2718;
    const N = MAX_COUNT;
    const idx = new Float32Array(N), rnd = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) { idx[i] = i; rnd[i * 3] = this._rnd(); rnd[i * 3 + 1] = this._rnd(); rnd[i * 3 + 2] = this._rnd(); }
    this._formU = {
      uN: { value: this._count }, uTitleN: { value: 0 }, uTitle: { value: null }, uBuild: { value: 0 }, uSpeed: { value: 1 }, uBands: { value: new THREE.Vector3(0.3, 0.3, 0.3) },
      uFormA: { value: 0 }, uFormB: { value: 0 }, uProg: { value: 1 }, uPhA: { value: 0 }, uPhB: { value: 0 }, uExplode: { value: 0 }
    };
    const shared = () => Object.assign({}, this._formU);
    this._objU = Object.assign(shared(), {
      uTime: { value: 0 }, uSize: { value: 0.22 }, uPulse: { value: new THREE.Vector3() }, uExposure: { value: 1 },
      uMatcap: { value: null }, uCol0: { value: new THREE.Color() }, uCol1: { value: new THREE.Color() }, uCol2: { value: new THREE.Color() }, uFlash: { value: 0 }, uType: { value: 0 }
    });
    this._chromeMat = new THREE.ShaderMaterial({ uniforms: this._objU, vertexShader: objectVertex, fragmentShader: chromeFragment });
    this._neonMat = new THREE.ShaderMaterial({ uniforms: this._objU, vertexShader: objectVertex, fragmentShader: neonFragment });
    const bases = [new THREE.BoxGeometry(1, 1, 1), new THREE.TetrahedronGeometry(0.85), new THREE.TorusGeometry(0.5, 0.17, 6, 14), new THREE.IcosahedronGeometry(0.6, 1)];
    this._meshes = bases.map((b) => { const m = new THREE.Mesh(instanced(b, idx, rnd), this._chromeMat); m.frustumCulled = false; m.visible = false; return m; });
    for (const b of bases) b.dispose();
    this._priv = new THREE.Scene();
    for (const m of this._meshes) this._priv.add(m);
    // the trails: 4 segments per object
    const tg = new THREE.InstancedBufferGeometry();
    const tp = new Float32Array(8 * 3), tk = new Float32Array(8 * 3);
    for (let k = 0; k < 4; k++) for (let e = 0; e < 2; e++) { const v = k * 2 + e; tk[v * 3] = k; tk[v * 3 + 1] = e; }
    tg.setAttribute('position', new THREE.BufferAttribute(tp, 3));
    tg.setAttribute('aK', new THREE.BufferAttribute(tk, 3));
    tg.setAttribute('aIdx', new THREE.InstancedBufferAttribute(idx, 1));
    tg.setAttribute('aRand', new THREE.InstancedBufferAttribute(rnd, 3));
    tg.instanceCount = this._count;
    this._trailU = Object.assign(shared(), { uDProg: { value: 0.02 }, uTrailCol: { value: new THREE.Color(0.5, 0.5, 0.5) }, uTrailK: { value: 0.35 } });
    this._trailMat = new THREE.ShaderMaterial({ uniforms: this._trailU, vertexShader: trailVertex, fragmentShader: trailFragment, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false });
    this._trails = new THREE.LineSegments(tg, this._trailMat); this._trails.frustumCulled = false; this._trails.visible = false;
    this._priv.add(this._trails);
    // the private target (MSAA at tiers 1-2) and the display
    this._rt = null;
    this._u = { uTex: { value: null }, uAspect: safeZoneUniforms.uAspect, uWhite: { value: 0 }, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._displayMat = new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: quadVertex, fragmentShader: displayFragment, depthTest: false, depthWrite: false });
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._displayMat);
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._size = new THREE.Vector2();
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    // state
    this._time = 0; this._free = 0; this._beats = 0; this._bars = 0; this._secondsBeat = 60 / 128;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null; this._beatPhase = 0;
    this._bands = new THREE.Vector3(0.3, 0.3, 0.3); this._kickAge = 9;
    this._formA = this._forceForm != null ? this._forceForm : 0; this._formB = this._formA; this._prog = 1; this._transS = 1;
    this._ph = [0, 0, 0, 0, 0, 0];                   // each formation's own phase clock
    this._nextForms = [];
    this._build = 0; this._explode = 0; this._dropUntil = -1; this._white = 0;
    this._type = 0; this._type2 = -1; this._mat = this._forceMat || 'chrome';
    this._titleDone = -1; this._title = null; this._titleKey = null;
    this._camMode = INSIDE[FORMS[this._formA]] ? 1 : 0; this._camBlend = this._camMode; this._azFixed = false;
    this._orbitAz = 0.3; this._orbitEl = 0.25; this._azTarget = 0.3; this._elTarget = 0.25; this._recoil = 0;
    this._paletteRef = undefined; this._palette = FALLBACK.slice();
    this._applyPalette();
    this._applyType();
    const fa0 = FRONT_AZ[FORMS[this._formA]];
    if (fa0 != null) { this._orbitAz = this._azTarget = fa0; this._orbitEl = this._elTarget = 0.12; this._azFixed = true; }
  }

  _applyPalette() {
    const cols = this._palette.slice(0, 3).map((h) => vdjColor(h));
    while (cols.length < 3) cols.push(cols[cols.length - 1].clone().offsetHSL(0.5, 0, 0));
    this._objU.uCol0.value.copy(cols[0]); this._objU.uCol1.value.copy(cols[1]); this._objU.uCol2.value.copy(cols[2]);
    this._trailU.uTrailCol.value.copy(cols[0]).lerp(cols[1], 0.5).multiplyScalar(0.6);
    if (this._objU.uMatcap.value) this._objU.uMatcap.value.dispose();
    // Totem's 'neon' kind: the studio's white light boxes over a body in the palette's two hues - chrome with neon reflections
    const m = genererMatcap('neon', cols, 256);
    const t = new THREE.DataTexture(m.data, m.size, m.size, THREE.RGBAFormat, THREE.UnsignedByteType);
    t.colorSpace = THREE.NoColorSpace; t.minFilter = t.magFilter = THREE.LinearFilter; t.generateMipmaps = false; t.needsUpdate = true;
    this._objU.uMatcap.value = t;
  }
  _applyType() {
    const mat = this._mat === 'neon' ? this._neonMat : this._chromeMat;
    for (let k = 0; k < this._meshes.length; k++) {
      const m = this._meshes[k];
      const on = k === this._type || k === this._type2;
      m.visible = on; m.material = mat;
      // a mixture: even indices one type, odd the other (half the instances each, interleaved by index)
      m.geometry.instanceCount = on ? (this._type2 >= 0 ? Math.ceil(this._count / 2) : this._count) : 0;
    }
    this._objU.uType.value = this._type;
    this._mixOffset = this._type2 >= 0 ? 1 : 0;
  }
  _setTitle(profile) {
    const key = profile.title || null;
    if (key === this._titleKey) return;
    this._titleKey = key;
    if (this._title) { this._title.texture.dispose(); this._title = null; }
    const text = (key || 'HALOFLOOR').toUpperCase().slice(0, 24);
    try { this._title = titlePoints(text, this._count); } catch (e) { this._title = null; }
    this._formU.uTitle.value = this._title ? this._title.texture : null;
    this._formU.uTitleN.value = this._title ? this._title.n : 0;
  }
  // the next formation: cycle without repeats; the title once per scene
  _nextFormation(sceneT) {
    if (this._forceForm != null) return this._forceForm;
    if (this._title && this._titleDone !== this._clock.cycle && sceneT > SCENE_S * 0.45) { this._titleDone = this._clock.cycle; return 5; }
    if (!this._nextForms.length) { this._nextForms = [0, 1, 2, 3, 4].filter((f) => f !== this._formB); for (let i = this._nextForms.length - 1; i > 0; i--) { const j = Math.floor(this._rnd() * (i + 1)); [this._nextForms[i], this._nextForms[j]] = [this._nextForms[j], this._nextForms[i]]; } }
    return this._nextForms.pop();
  }
  _goTo(form, beats) {
    this._formA = this._formB; this._formU.uPhA.value = this._ph[this._formA];
    this._formB = form; this._prog = 0; this._transS = Math.max(0.3, beats * this._secondsBeat);
    this._camMode = INSIDE[FORMS[form]] ? 1 : 0;
    const fa = FRONT_AZ[FORMS[form]];
    if (fa != null) { this._azTarget = fa + (this._rnd() - 0.5) * 0.2; this._elTarget = 0.08 + 0.1 * this._rnd(); this._azFixed = true; } else this._azFixed = false;
  }

  update(deltaTime, profile) {
    if (profile.artworkPalette !== this._paletteRef) {
      this._paletteRef = profile.artworkPalette;
      const v = profile.artworkPalette && profile.artworkPalette.vividColors && profile.artworkPalette.vividColors.length ? profile.artworkPalette.vividColors : FALLBACK;
      this._palette = v.slice(0, 3); this._applyPalette();
    }
    this._setTitle(profile);
    const playing = profile.playbackState === undefined || profile.playbackState === 'PLAYING';
    const dt = playing ? Math.min(deltaTime, 0.1) : 0;
    this._clock.update(dt);
    if (dt === 0) return;
    this._time += dt;
    const director = profile.director;
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / bpm : 60 / 128;
    const barS = 4 * this._secondsBeat;
    let beat = false, bar = false, phrase = false;
    const bp = profile.beatPhase;
    if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; this._beatPhase = bp; }
    else { this._free += dt; if (this._free >= this._secondsBeat) { this._free -= this._secondsBeat; beat = true; } this._beatPhase = this._free / this._secondsBeat; }
    const barPhase = profile.barPhase;
    if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; }
    else if (beat && (this._beats + 1) % 4 === 0) bar = true;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
    if (beat) this._beats++;
    if (bar) this._bars++;
    const synth = Math.exp(-this._beatPhase * 6);
    const target = bands ? new THREE.Vector3(clamp01(bands.low), clamp01(bands.mid), clamp01(bands.high)) : new THREE.Vector3(synth, 0.5 * synth + 0.2, 0.3 + 0.3 * Math.sin(this._time * 7.0));
    this._bands.lerp(target, Math.min(1, dt / 0.05));

    // the build and the drop
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._build += (build - this._build) * (1 - Math.exp(-dt / 0.8));
    const dropNow = next && next.type === 'drop' && next.msUntil != null && next.msUntil <= dt * 500 + 30 && profile.estimatedPosition != null;
    if (dropNow) {
      const key = Math.round((profile.estimatedPosition * 1000 + next.msUntil) / 100);
      if (key !== this._lastDropKey) {
        this._lastDropKey = key;
        this._explode = 1; this._dropUntil = this._time + 4 * barS; this._white = 0.6;
        if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
        this._goTo(2, 1);                              // then the tunnel, at full speed
      }
    }
    this._explode *= Math.pow(0.5, dt / (0.35 * this._secondsBeat));
    this._white *= Math.pow(0.5, dt / 0.08);
    const inDrop = this._time < this._dropUntil;
    const sceneT = this._clock.t;

    // BAR: the next formation (the build holds the sphere)
    if (bar && this._prog >= 1 && !inDrop) {
      if (this._build > 0.4) { if (this._formB !== 0) this._goTo(0, 1); }
      else { const nf = this._nextFormation(sceneT); if (nf !== this._formB || this._forceForm == null) this._goTo(nf, 1); }
    } else if (this._clock.wrapped && this._prog >= 1 && !inDrop) this._goTo(this._nextFormation(sceneT), 1);
    // PHRASE: the object type (or a mixture), the material, the palette shift
    if (phrase && this._forceMat == null) {
      this._type = Math.floor(this._rnd() * 4);
      this._type2 = this._rnd() < 0.4 ? (this._type + 1 + Math.floor(this._rnd() * 3)) % 4 : -1;
      this._mat = this._rnd() < 0.5 ? 'chrome' : 'neon';
      this._applyType();
      this._palette.push(this._palette.shift()); this._applyPalette();
    } else if (phrase) { this._type = Math.floor(this._rnd() * 4); this._type2 = -1; this._applyType(); }
    // the transition's progress
    const prevProg = this._prog;
    if (this._prog < 1) this._prog = Math.min(1, this._prog + dt / this._transS);
    const speed = (1 + 3 * this._build) * (inDrop ? 3 : 1);
    for (let f = 0; f < 6; f++) this._ph[f] += dt * speed * (f === 0 ? 0.25 : f === 1 ? 0.9 : f === 2 ? 1.6 : f === 3 ? 0.8 : f === 4 ? 0.7 : 0.3);
    // BEAT: the size pulse per group, the neon flash, the camera recoil
    if (beat) { this._kickAge = 0; this._recoil = 0.03; }
    this._kickAge += dt;
    const env = this._kickAge < KICK_ATTACK ? this._kickAge / KICK_ATTACK : Math.exp(-(this._kickAge - KICK_ATTACK) / KICK_TAU);
    this._objU.uPulse.value.set(this._bands.x * env, this._bands.y * env, this._bands.z * env);
    this._objU.uFlash.value = env;
    this._recoil *= Math.pow(0.5, dt / 0.12);
    // PHRASE: a new camera plan
    if (phrase) { this._azTarget += 0.8 + 1.2 * this._rnd(); this._elTarget = 0.1 + 0.4 * this._rnd(); }
    this._orbitAz += (this._azTarget - this._orbitAz) * (1 - Math.exp(-dt / 1.5)) + (this._azFixed ? 0 : dt * 0.08);
    if (!this._azFixed) this._azTarget += dt * 0.08;
    this._orbitEl += (this._elTarget - this._orbitEl) * (1 - Math.exp(-dt / 1.5));
    this._camBlend += (this._camMode - this._camBlend) * Math.min(1, dt / this._transS);

    // the uniforms
    const F = this._formU;
    F.uN.value = this._count; F.uFormA.value = this._formA; F.uFormB.value = this._formB; F.uProg.value = this._prog;
    F.uPhA.value = this._ph[this._formA]; F.uPhB.value = this._ph[this._formB];
    F.uBuild.value = this._build; F.uSpeed.value = speed; F.uBands.value.copy(this._bands); F.uExplode.value = this._explode;
    this._objU.uTime.value = this._time;
    const textK = lerp(this._formA === 5 ? 0.55 : 1, this._formB === 5 ? 0.55 : 1, this._prog);   // smaller objects spell a legible title
    this._objU.uSize.value = 0.22 * Math.sqrt(1500 / this._count) * (1 - 0.3 * this._build) * textK;
    this._trails.visible = this._layout.trails && this._prog < 1 && this._prog > 0.02;
    this._trailU.uDProg.value = Math.max(this._prog - prevProg, 0.004);
    this._u.uWhite.value = this._white;
    this._post.update(vdjBeatChroma(this._beatPhase, this._secondsBeat));
    // the camera: outside (orbit) or inside (near the axis), blended
    const cam = this._camera;
    // in front (wall, vortex, title) the camera comes closer: the formation fills the frame
    const R = (this._azFixed ? 4.5 : 7.5) * (1 + this._recoil);
    const outside = new THREE.Vector3(Math.cos(this._orbitAz) * Math.cos(this._orbitEl), Math.sin(this._orbitEl), Math.sin(this._orbitAz) * Math.cos(this._orbitEl)).multiplyScalar(R);
    const inside = new THREE.Vector3(0.6 * Math.sin(this._time * 0.4), 0.5 * Math.sin(this._time * 0.31 + 1.0), 2.5 * (1 + this._recoil));
    const pos = outside.lerp(inside, this._camBlend);
    const look = new THREE.Vector3(0, 0, 0).lerp(new THREE.Vector3(0.2 * Math.sin(this._time * 0.2), 0.1 * Math.sin(this._time * 0.27), -10), this._camBlend);
    cam.position.copy(pos); cam.up.set(0, 1, 0); cam.lookAt(look); cam.fov = 60; cam.updateProjectionMatrix();
  }

  _render(renderer) {
    renderer.getDrawingBufferSize(this._size);
    const W = Math.max(2, this._size.x), H = Math.max(2, this._size.y);
    if (!this._rt || this._rt.width !== W || this._rt.height !== H) {
      if (this._rt) this._rt.dispose();
      this._rt = new THREE.WebGLRenderTarget(W, H, { samples: this._layout.msaa, depthBuffer: true, type: THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false });
      this._u.uTex.value = this._rt.texture;
    }
    const cam = this._camera;
    cam.aspect = W / H; cam.updateProjectionMatrix();
    const prev = renderer.getRenderTarget(), auto = renderer.autoClear, cc = renderer.getClearColor(new THREE.Color()), ca = renderer.getClearAlpha();
    renderer.setRenderTarget(this._rt);
    renderer.setClearColor(0x000000, 1); renderer.autoClear = true;
    renderer.render(this._priv, cam);
    renderer.setRenderTarget(prev); renderer.autoClear = auto; renderer.setClearColor(cc, ca);
  }

  get probe() {
    return { t: +this._time.toFixed(2), formA: FORMS[this._formA], formB: FORMS[this._formB], prog: +this._prog.toFixed(2), type: this._type, type2: this._type2, mat: this._mat,
      build: +this._build.toFixed(2), explode: +this._explode.toFixed(2), cam: +this._camBlend.toFixed(2), count: this._count, title: this._title ? this._title.n : 0, bars: this._bars, scene: +this._clock.t.toFixed(1) };
  }

  dispose() {
    if (this._display) { this._display.onBeforeRender = null; if (this._scene) this._scene.remove(this._display); this._display.geometry.dispose(); this._displayMat.dispose(); }
    for (const m of this._meshes || []) m.geometry.dispose();
    if (this._trails) this._trails.geometry.dispose();
    if (this._chromeMat) this._chromeMat.dispose(); if (this._neonMat) this._neonMat.dispose(); if (this._trailMat) this._trailMat.dispose();
    if (this._objU && this._objU.uMatcap.value) this._objU.uMatcap.value.dispose();
    if (this._title) this._title.texture.dispose();
    if (this._rt) this._rt.dispose();
    this._scene = null;
  }
}
